import { Enums } from '@configur-tech/upit-core-types';
import { cloneDeep } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Popup } from 'semantic-ui-react';
import { DefaultPopupStyles } from '../../main/theme';
import { DatasetMetaItemOutput } from '../../services/dataset-meta/DatasetMetaService';
import { RootState } from '../../store/rootReducer';
import DroppableComp from '../DragAndDrop/DroppableComp';
import { AggregateStage } from '../ProjectItem/ProjectItemAggregationStage/3-aggregate/ProjectItemAggregationAggregateStage';
import AggregationOptionButton from './AggregationOptionButton';
import * as SC from './styled';

export interface SortItem {
  field: string;
  alias: string;
  datasetMetaId: string;
  direction: Enums.ProjectionSortOrder;
  active?: boolean;
  isMeasure?: boolean;
}

interface AggregationSortProps {
  sortItems: SortItem[];
  onChange: (sortItems: SortItem[]) => void;
  disabled?: boolean;
  singleList?: boolean;
  hideDirectionButton?: boolean;
}
const AggregationSort: FC<AggregationSortProps> = ({
  sortItems,
  onChange,
  disabled,
  singleList,
  hideDirectionButton,
}) => {
  const datasetMetasState = useSelector(
    (state: RootState) => state.datasetMetas,
  );
  const datasetMetas: DatasetMetaItemOutput[] = datasetMetasState.data.data;

  const [orderedSortItems, setOrderedSortItems] = useState<SortItem[]>([]);

  useEffect(() => {
    if (sortItems.length) {
      setOrderedSortItems(sortItems.filter((item) => item.active));
    }
  }, [sortItems]);

  if (!sortItems?.length) {
    return null;
  }

  const onCheckedChange = (itemAlias: string, checked: boolean) => {
    const cloned: SortItem[] = cloneDeep(sortItems);
    const item = cloned.find(
      (item) => item.alias === itemAlias || item.field === itemAlias,
    );

    if (item) {
      item.active = checked;
      onChange(cloned);
    }
  };

  const onDirChange = (
    itemAlias: string,
    direction: Enums.ProjectionSortOrder,
  ) => {
    const cloned: SortItem[] = cloneDeep(sortItems);
    const item = cloned.find(
      (item) => item.alias === itemAlias || item.field === itemAlias,
    );
    if (item) {
      item.direction = direction;
      onChange(cloned);
    }
  };

  const reorderItems = (list, startIndex, endIndex): SortItem[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result as SortItem[];
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const sortedItems = reorderItems(
      orderedSortItems,
      result.source.index,
      result.destination.index,
    );

    setOrderedSortItems(sortedItems);

    !singleList
      ? onChange(sortedItems)
      : onChange(
          sortedItems.concat(sortItems.filter((i) => i.active === false)),
        );
  };

  const buildSortItemButton = (
    item: SortItem,
    index: number,
    dragContextProvided?,
  ) => {
    return (
      <Popup
        content={
          datasetMetas?.find((d) => d.entity._id === item.datasetMetaId)?.entity
            .name
        }
        position="left center"
        style={DefaultPopupStyles}
        hideOnScroll={true}
        trigger={
          <div>
            {dragContextProvided && (
              <SC.SortButton
                disabled={disabled}
                active={item.active}
                ref={dragContextProvided.innerRef}
                {...dragContextProvided.draggableProps}
                {...dragContextProvided.dragHandleProps}
              >
                <AggregationOptionButton
                  type={AggregateStage.GROUP}
                  item={item}
                  index={index}
                  onCheckedChange={onCheckedChange}
                  onDirChange={onDirChange}
                  disabled={disabled}
                  hideDirectionButton={hideDirectionButton}
                />
              </SC.SortButton>
            )}

            {!dragContextProvided && (
              <SC.SortButton disabled={disabled} active={item.active}>
                <AggregationOptionButton
                  type={AggregateStage.SORT}
                  item={item}
                  index={index}
                  onCheckedChange={onCheckedChange}
                  onDirChange={onDirChange}
                  disabled={disabled}
                  hideDirectionButton={hideDirectionButton}
                />
              </SC.SortButton>
            )}
          </div>
        }
      />
    );
  };

  return (
    <>
      {orderedSortItems.length > 0 && (
        <div>
          {!singleList && (
            <SC.StyledPanelSubHeader>Sorted</SC.StyledPanelSubHeader>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <DroppableComp
              dropComp={
                <Droppable droppableId="droppable-sort">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {orderedSortItems.map((item, index) => (
                        <Draggable
                          isDragDisabled={disabled}
                          key={`${item.field}-${index}`}
                          draggableId={`${item.field}-${index}`}
                          index={index}
                        >
                          {(provided) =>
                            buildSortItemButton(item, index, provided)
                          }
                        </Draggable>
                      ))}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              }
            />
          </DragDropContext>
        </div>
      )}
      <div>
        {sortItems.filter((item) => !item.active).length > 0 && (
          <>
            {!singleList && (
              <SC.StyledPanelSubHeader>Unsorted</SC.StyledPanelSubHeader>
            )}
            {sortItems
              .filter((item) => !item.active)
              .map((item, index) => buildSortItemButton(item, index))}
          </>
        )}
      </div>
    </>
  );
};

export default AggregationSort;
